import { useEffect, useState } from "react";
import Layout from "@/components/layout/Layout";
import Button from "@/components/ui/Button";
import TextField from "@/components/ui/Textfield";
import PopoverMenu from "@/components/ui/Popeover";
import { BiSearch } from "react-icons/bi";
import { Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";
import useRoleStore from "./store/rolesStore";
import useGlobalStore from "@/store/globalStore";
import { useShallow } from "zustand/react/shallow";
import { getColorByHash } from "@/helper/utils";

type ScopeFilter = "all" | "global" | "media";

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
}

export default function ManageRoles() {
  const navigate  = useNavigate();
  const [isLoading, setIsLoading]     = useState(false);
  const [search, setSearch]           = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>("all");

  const { roles, getRoles, setRole, sortField } = useRoleStore(
    useShallow(({ roles, getRoles, setRole, sortField }) => ({
      roles, getRoles, setRole, sortField,
    }))
  );
  const showError = useGlobalStore((s) => s.showError);

  const itemsPerPage = 25;

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try   { await getRoles(); }
      catch (e: any) { showError(e?.message ?? "Something went wrong"); }
      finally { setIsLoading(false); }
    })();
  }, []);

  const filtered = (roles ?? []).filter((r) => {
    const matchSearch = r.name.toLowerCase().includes(search.toLowerCase()) || r.id.includes(search);
    const matchScope  = scopeFilter === "all" || r.scope === scopeFilter;
    return matchSearch && matchScope;
  });

  const totalPages    = Math.ceil(filtered.length / itemsPerPage);
  const startIndex    = (currentPage - 1) * itemsPerPage;
  const currentRoles  = filtered.slice(startIndex, startIndex + itemsPerPage);

  const handleEdit = (roleId: string) => {
    const role = (roles ?? []).find((r) => r.id === roleId);
    if (!role) { showError("Role not found."); return; }
    setRole(role);
    navigate(`/roles/permissions?id=${roleId}`);
  };

  const handleSort = (field: string) => {
    let newSort: string;
    if (sortField === field) {
      newSort = `-${field}`;
    } else if (sortField === `-${field}`) {
      newSort = '';
    } else {
      newSort = field;
    }
    setCurrentPage(1);
    getRoles(newSort || undefined);
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField === field) {
      return (
        <svg className="w-4 h-4 text-pink-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M6 15l6-6 6 6" />
        </svg>
      );
    }
    if (sortField === `-${field}`) {
      return (
        <svg className="w-4 h-4 text-pink-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M6 9l6 6 6-6" />
        </svg>
      );
    }
    return null;
  };

  const scopeBtnCls = (s: ScopeFilter) =>
    `px-3 py-1.5 text-xs rounded-full border transition-colors cursor-pointer select-none
     ${scopeFilter === s
       ? "bg-[#EA33C0] border-[#EA33C0] text-white font-medium"
       : "border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400"}`;

  return (
    <Layout>
      <h1 className="text-2xl font-bold mb-8">Manage Roles</h1>

      <div className="flex justify-between mb-6">
        <div className="flex items-center gap-3">
          <TextField
            type="text"
            leadingIcon={<BiSearch />}
            placeholder="Search by keyword, name, etc."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            className="w-96"
          />
          <div className="flex gap-1.5">
            {(["all", "global", "media"] as ScopeFilter[]).map((s) => (
              <button key={s} className={scopeBtnCls(s)} onClick={() => { setScopeFilter(s); setCurrentPage(1); }}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 border-b border-gray-300 pb-2 mb-2 text-sm text-gray-700 select-none">
        <div className="col-span-4 flex items-center cursor-pointer gap-1" onClick={() => handleSort('name')}>
          Role
          <SortIcon field="name" />
        </div>
        <div className="col-span-2 flex items-center cursor-pointer gap-1" onClick={() => handleSort('scope')}>
          Scope
          <SortIcon field="scope" />
        </div>
        <div className="col-span-2 flex items-center cursor-pointer gap-1" onClick={() => handleSort('active')}>
          Status
          <SortIcon field="active" />
        </div>
        <div className="col-span-3 flex items-center cursor-pointer gap-1" onClick={() => handleSort('created_at')}>
          Created
          <SortIcon field="created_at" />
        </div>
        <div className="col-span-1" />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <div className="w-6 h-6 border-2 border-[#EA33C0] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : currentRoles.length === 0 ? (
        <div className="text-center text-gray-400 py-16 text-sm">
          No roles match your search.
        </div>
      ) : (
        <div className="divide-y divide-gray-300">
          {currentRoles.map((role) => (
            <div
              key={role.id}
              className="grid grid-cols-12 py-3 text-sm items-center cursor-pointer"
              onClick={() => handleEdit(role.id)}
            >
              <div className="col-span-4 flex items-center gap-3">
                <div className={`flex items-center justify-center w-12 h-12 rounded-full text-white font-semibold text-lg ${getColorByHash(role.name)}`}>
                  {role.name.slice(0, 1).toUpperCase()}
                </div>
                <div className="font-semibold">{role.name}</div>
              </div>

              <div className="col-span-2">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  role.scope === "global"
                    ? "bg-purple-100 text-purple-700"
                    : "bg-emerald-100 text-emerald-700"
                }`}>
                  {role.scope.charAt(0).toUpperCase() + role.scope.slice(1)}
                </span>
              </div>

              <div className="col-span-2 flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${role.active ? "bg-green-500" : "bg-gray-300"}`} />
                <span className={`text-sm ${role.active ? "text-green-500 font-semibold" : "text-gray-400"}`}>
                  {role.active ? "Active" : "Inactive"}
                </span>
              </div>

              <div className="col-span-3 text-sm text-gray-500">
                {formatDate(role.created_at)}
              </div>

              <div className="col-span-1 flex justify-end" onClick={(e) => e.stopPropagation()}>
                <PopoverMenu
                  items={[
                    {
                      label: "Edit Permissions",
                      showDivider: false,
                      icon: <Settings height={18} width={18} />,
                      onClick: () => handleEdit(role.id),
                    },
                  ]}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-end items-center mt-6 space-x-4 text-sm">
        <Button
          type="secondary"
          onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          disabled={currentPage === 1}
        >
          Previous
        </Button>
        <span>Page {currentPage} of {totalPages || 1}</span>
        <Button
          type="secondary"
          onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
          disabled={currentPage === totalPages || totalPages === 0}
        >
          Next
        </Button>
      </div>
    </Layout>
  );
}
